import React, { createContext, useContext, useState, useEffect } from 'react'
import { node } from 'prop-types'
import theme from '../gatsby-plugin-theme-ui/index'
import useHasMounted from './useHasMounted'

// translates window width in pixels to breakpoint values using breakpoint values from the theme.
function getBreakPoint(windowWidth) {
  if (windowWidth) {
    if (windowWidth < theme.breakpointValues[0]) {
      return 'mobile'
    } else if (windowWidth < theme.breakpointValues[1]) {
      return 'tablet'
    } else {
      return 'desktop'
    }
  }
}

// custom hook that returns the breakpoint based on window width
// also returns the raw window width value if needed
const useWindowWidth = () => {
  const isWindowClient = typeof window !== 'undefined'

  const [windowWidth, setWindowWidth] = useState(
    isWindowClient ? window.innerWidth : 1200
  )

  const [breakpoint, setBreakpoint] = useState(
    isWindowClient ? getBreakPoint(window.innerWidth) : getBreakPoint(1200)
  )

  useEffect(() => {
    const setSize = () => {
      setWindowWidth(window.innerWidth)
      setBreakpoint(getBreakPoint(window.innerWidth))
    }

    isWindowClient && window.addEventListener('resize', setSize)
    return () => window.removeEventListener('resize', setSize)
  }, [isWindowClient, setWindowWidth])

  return { breakpoint, windowWidth }
}

const windowWidthContext = createContext({})

// context provider component that uses the window width and provides it to all children
const WindowWidthProvider = ({ children }) => {
  const { breakpoint, windowWidth } = useWindowWidth()

  // to prevent rehydration issues, re-render once after mount
  const hasMounted = useHasMounted()
  if (!hasMounted) {
    return null
  }

  return (
    <windowWidthContext.Provider value={{ breakpoint, windowWidth }}>
      {children}
    </windowWidthContext.Provider>
  )
}

WindowWidthProvider.propTypes = {
  children: node,
}

// use this in any consumer of windowWidthContext to use the breakpoint
const useBreakpoint = () => {
  const { breakpoint } = useContext(windowWidthContext)

  return breakpoint
}

// use this in any consumer of windowWidthContext to use the viewport width
const useViewportWidth = () => {
  const { windowWidth } = useContext(windowWidthContext)
  return windowWidth
}

export { WindowWidthProvider, useBreakpoint, useViewportWidth }
